import React from "react";
import { Box, Stack, Typography, Chip, alpha } from "@mui/material";
import { formatDateTime } from "../../utils/time";

const formatLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isEmpty = (val) =>
  val === null ||
  val === undefined ||
  val === "" ||
  (Array.isArray(val) && val.length === 0);

export const renderChangeValue = (val) => {
  if (isEmpty(val)) {
    return (
      <Typography
        component="span"
        sx={{ color: "#94A3B8", fontStyle: "italic", fontSize: "0.78rem" }}
      >
        —
      </Typography>
    );
  }

  if (typeof val === "boolean") {
    return (
      <Chip
        label={val ? "Yes" : "No"}
        size="small"
        sx={{
          height: 20,
          fontSize: "0.65rem",
          fontWeight: 600,
          bgcolor: val ? alpha("#22C55E", 0.1) : alpha("#94A3B8", 0.1),
          color: val ? "#22C55E" : "#64748B",
        }}
      />
    );
  }

  if (typeof val === "number") {
    return (
      <Typography
        component="span"
        sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1A1A2E" }}
      >
        {val.toLocaleString()}
      </Typography>
    );
  }

  if (typeof val === "string") {
    if (/\d{4}-\d{2}-\d{2}T/.test(val)) {
      return (
        <Typography component="span" sx={{ fontSize: "0.78rem", color: "#475467" }}>
          {formatDateTime(val)}
        </Typography>
      );
    }

    if (val.startsWith("http")) {
      return (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#2469F0",
            fontSize: "0.78rem",
            textDecoration: "underline",
          }}
        >
          {val}
        </a>
      );
    }

    return (
      <Typography component="span" sx={{ fontSize: "0.78rem", color: "#475467" }}>
        {val}
      </Typography>
    );
  }

  if (Array.isArray(val)) {
    if (val.every((v) => typeof v !== "object" || v === null)) {
      return (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {val.map((v, i) => (
            <Chip
              key={i}
              label={String(v)}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: alpha("#2469F0", 0.08),
                color: "#2469F0",
              }}
            />
          ))}
        </Stack>
      );
    }

    return (
      <Stack spacing={1}>
        {val.map((item, index) => (
          <Box
            key={index}
            sx={{
              p: 1.5,
              bgcolor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
            }}
          >
            <Chip
              label={`#${index + 1}`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.6rem",
                fontWeight: 700,
                bgcolor: alpha("#2469F0", 0.08),
                color: "#2469F0",
                mb: 1,
              }}
            />
            <Stack spacing={0.5}>
              {Object.entries(item).map(([key, subVal]) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    py: 0.5,
                    borderBottom: "1px dashed #F1F5F9",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#94A3B8",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      flexShrink: 0,
                    }}
                  >
                    {formatLabel(key)}
                  </Typography>
                  <Box sx={{ textAlign: "right", minWidth: 0 }}>
                    {renderChangeValue(subVal)}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  }

  if (typeof val === "object") {
    if (val.display || val.name || val.first_name || val.last_name) {
      return (
        <Typography component="span" sx={{ fontSize: "0.78rem", color: "#475467" }}>
          {val.display ||
            val.name ||
            [val.first_name, val.last_name].filter(Boolean).join(" ") ||
            "—"}
        </Typography>
      );
    }

    return (
      <Stack spacing={0.5}>
        {Object.entries(val).map(([key, subVal]) => (
          <Box
            key={key}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              py: 0.5,
              borderBottom: "1px dashed #F1F5F9",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#94A3B8",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                flexShrink: 0,
              }}
            >
              {formatLabel(key)}
            </Typography>
            <Box sx={{ textAlign: "right", minWidth: 0 }}>
              {renderChangeValue(subVal)}
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Typography component="span" sx={{ fontSize: "0.78rem", color: "#475467" }}>
      {String(val)}
    </Typography>
  );
};

export const summarizeChanges = (data) => {
  if (!data || typeof data !== "object") return "-";

  const fields = Object.keys(data).slice(0, 3);

  const summary = fields
    .map((f) => {
      const change = data[f] || {};
      const oldVal =
        change.old !== undefined ? stringifyValue(change.old) : "-";
      const newVal =
        change.new !== undefined ? stringifyValue(change.new) : "-";

      return `${formatLabel(f)}: ${oldVal} → ${newVal}`;
    })
    .join(", ");

  return summary || "-";
};

const stringifyValue = (val) => {
  if (isEmpty(val)) return "—";

  if (typeof val === "boolean") return val ? "Yes" : "No";

  if (typeof val === "string") {
    if (/\d{4}-\d{2}-\d{2}T/.test(val)) return formatDateTime(val);
    return val;
  }

  if (typeof val === "number") return val.toLocaleString();

  if (Array.isArray(val)) {
    if (val.every((v) => typeof v !== "object" || v === null)) {
      return val.join(", ");
    }
    return `${val.length} items`;
  }

  if (typeof val === "object") {
    return (
      val.display ||
      val.name ||
      [val.first_name, val.last_name].filter(Boolean).join(" ") ||
      `${Object.keys(val).length} fields`
    );
  }

  return String(val);
};